<script setup>
// The frame of every Polaris AI page, on the kit's plant (src/product/areas/AreaPage.vue): the kit's PageHeader
// (a short title, "learn more", the page's actions), the body that scrolls under it, the kit's PaginationFooter at
// the end of the list (as the kit's HelpListPage does), and room beside the body for a drawer column (`aside`,
// from 1280px; below that a drawer is an overlay and the slot takes no room).
// Phase 4.1: the big title and the "Back" button left (the Topbar's breadcrumb says where the page is, D-G2).
// Phase 4.2: `invitation` ({ title, text, actionLabel? }) shows the kit's Invitation at the top of the body, with the
// Polaris animation (D-G3). Dismissing it is remembered in the person's account (ui_settings
// `polaris_dismissed_invitations`, by `invitationKey`, R2-4-1 = B), so it stays away on every device. Its action is
// the page's primary action (the `click` event), shown only to whoever may use it (`buttonPolicy`).
// The side preview (PolarisPreviewPanel) sits at the right of the page when `preview` is 'open' (edit pages: it
// starts open) or 'folded' (reading pages: it starts as a rail); 'off' (the default) has none. The person's own
// choice, remembered in the browser, wins over this default.
// Same props, slots and events as before, so the pages did not change contract. `backUrl` is still accepted and
// does nothing (no page passes it any more).
import { computed, useSlots, watch } from 'vue';
import { onBeforeRouteLeave, useRoute } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { useUISettings } from 'dashboard/composables/useUISettings';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Policy from 'dashboard/components/policy.vue';
import PageHeader from 'dashboard/components-next/polaris/cards/PageHeader.vue';
import PaginationFooter from 'dashboard/components-next/polaris/cards/PaginationFooter.vue';
import Invitation from 'dashboard/components-next/polaris/cards/Invitation.vue';
import PolarisPreviewPanel from 'dashboard/components-next/polaris/preview/PolarisPreviewPanel.vue';
import SetupProgressBar from 'dashboard/components-next/polaris/pageComponents/setup/SetupProgressBar.vue';
import { SETUP_STEP_ROUTE_NAMES } from 'dashboard/routes/dashboard/polaris/setupSteps';

const props = defineProps({
  currentPage: { type: Number, default: 1 },
  totalCount: { type: Number, default: 100 },
  itemsPerPage: { type: Number, default: 25 },
  headerTitle: { type: String, default: '' },
  // eslint-disable-next-line vue/no-unused-properties
  backUrl: { type: [String, Object], default: '' },
  buttonPolicy: { type: Array, default: () => [] },
  buttonLabel: { type: String, default: '' },
  featureFlag: { type: String, default: '' },
  isFetching: { type: Boolean, default: false },
  showKnowMore: { type: Boolean, default: true },
  isEmpty: { type: Boolean, default: false },
  showPaginationFooter: { type: Boolean, default: true },
  // The body fills the height instead of scrolling (the Playground: its own conversation scrolls).
  fill: { type: Boolean, default: false },
  invitation: { type: Object, default: null },
  invitationKey: { type: String, default: '' },
  // off | open | folded: the side preview and how it starts
  preview: {
    type: String,
    default: 'off',
    validator: value => ['off', 'open', 'folded'].includes(value),
  },
});

const emit = defineEmits(['click', 'close', 'update:currentPage']);

const slots = useSlots();
const { shouldShowPaywall, checkPermissions } = usePolicy();
const { uiSettings, updateUISettings } = useUISettings();
const route = useRoute();
const store = useStore();

// Barra "Passo N de 6" do setup (Fase 1): nas páginas de um assistente, enquanto o setup não está completo.
// O Get started tem a checklist própria, então não leva a barra. O mapa passo→rota é o setupSteps.js.
const setupAssistantId = computed(
  () => Number(route.params.assistantId) || null
);
const setupStatus = computed(() =>
  setupAssistantId.value
    ? store.getters['polarisSetupStatus/getStatus'](setupAssistantId.value)
    : null
);
const showSetupProgress = computed(
  () =>
    !!setupStatus.value &&
    !setupStatus.value.completed &&
    SETUP_STEP_ROUTE_NAMES.has(route.name)
);
const setupAssistantName = computed(
  () =>
    store.getters['polarisAssistants/getRecord'](setupAssistantId.value)
      ?.name || ''
);
watch(
  setupAssistantId,
  id => {
    if (id) store.dispatch('polarisSetupStatus/fetch', id);
  },
  { immediate: true }
);
// Ao sair de uma página de passo (criar cenário, ligar inbox, salvar orientação...), força o refetch do status
// para os pontos do menu refletirem o passo recém-concluído — ponto único, em vez de um refetch por mutation.
onBeforeRouteLeave((to, from, next) => {
  if (setupAssistantId.value && SETUP_STEP_ROUTE_NAMES.has(from.name)) {
    store.dispatch('polarisSetupStatus/fetch', setupAssistantId.value, {
      force: true,
    });
  }
  next();
});

const showPaywall = computed(() => shouldShowPaywall(props.featureFlag));
const showLearn = computed(
  () => !props.isEmpty && props.showKnowMore && !!slots.knowMore
);
const showButton = computed(() => !showPaywall.value && !!props.buttonLabel);
const hasActions = computed(
  () => showButton.value || !!slots.headerActions || !!slots.search
);
// The footer only has something to say once the list is there.
const showFooter = computed(
  () =>
    props.showPaginationFooter &&
    !showPaywall.value &&
    !props.isFetching &&
    !props.isEmpty &&
    Number.isFinite(props.totalCount)
);

const DISMISSED = 'polaris_dismissed_invitations';
const dismissed = computed(() => uiSettings.value?.[DISMISSED] ?? []);
const showInvitation = computed(
  () =>
    !!props.invitation &&
    !showPaywall.value &&
    !dismissed.value.includes(props.invitationKey)
);
const invitationAction = computed(() =>
  showButton.value && checkPermissions(props.buttonPolicy)
    ? props.invitation?.actionLabel || ''
    : ''
);
const dismissInvitation = () => {
  updateUISettings({
    [DISMISSED]: [...new Set([...dismissed.value, props.invitationKey])],
  });
};

const handlePageChange = page => {
  emit('update:currentPage', page);
};
</script>

<template>
  <section
    class="flex h-full w-full min-w-0 flex-col overflow-hidden bg-n-surface-1 text-polaris-foreground"
    data-polaris-page
  >
    <div class="flex min-h-0 w-full flex-1">
      <div class="flex min-w-0 flex-1 flex-col">
        <PageHeader
          v-if="headerTitle"
          :title="headerTitle"
          :divider="!$slots.tabs"
        >
          <template v-if="showLearn" #learn>
            <slot name="knowMore" />
          </template>
          <template v-if="hasActions" #actions>
            <slot name="headerActions" />
            <slot name="search" />
            <div
              v-if="showButton"
              v-on-clickaway="() => emit('close')"
              class="relative"
            >
              <Policy :permissions="buttonPolicy">
                <Button
                  :label="buttonLabel"
                  icon="i-lucide-plus"
                  size="sm"
                  @click="emit('click')"
                />
              </Policy>
              <slot name="action" />
            </div>
          </template>
        </PageHeader>
        <slot name="tabs" />
        <SetupProgressBar
          v-if="showSetupProgress"
          :steps="setupStatus.steps"
          :assistant-name="setupAssistantName"
        />
        <main
          class="min-h-0 flex-1 px-6 py-6"
          :class="fill ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'"
        >
          <div
            class="mx-auto flex w-full max-w-5xl flex-col gap-6"
            :class="fill ? 'min-h-0 flex-1' : ''"
          >
            <Invitation
              v-if="showInvitation"
              :title="invitation.title"
              :text="invitation.text"
              :action-label="invitationAction"
              @action="emit('click')"
              @dismiss="dismissInvitation"
            />
            <slot v-if="!showPaywall" name="subHeader" />
            <slot v-if="!showPaywall" name="controls" />
            <div
              v-if="isFetching"
              class="flex items-center justify-center py-10 text-polaris-muted-foreground"
            >
              <Spinner />
            </div>
            <div v-else-if="showPaywall">
              <slot name="paywall" />
            </div>
            <div v-else-if="isEmpty">
              <slot name="emptyState" />
            </div>
            <slot v-else name="body" />
            <PaginationFooter
              v-if="showFooter"
              :page="currentPage"
              :total="totalCount"
              :page-size="itemsPerPage"
              @update:page="handlePageChange"
            />
            <slot />
          </div>
        </main>
      </div>
      <slot name="aside" />
      <PolarisPreviewPanel
        v-if="preview !== 'off'"
        :default-open="preview === 'open'"
      />
    </div>
  </section>
</template>
